import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import { useAuth } from '../../context/AuthContext';
import { ThumbsUp, Heart, Flame, Award, MessageCircle, Image as ImageIcon,
  Loader2, Send, X, TrendingUp, Users, Sparkles, Trash2 } from 'lucide-react';
import { SkeletonPost, SkeletonAvatar, Skeleton } from '../../components/Skeleton';
import SEO from '../../components/SEO';
import { MobilePhotoUploadButton } from '../../components/MobilePhotoCropModal';
import GuidedUpsell from '../../components/GuidedUpsell';
import { trackCommunityPostCreated } from '../../lib/analytics';
import { notifyError } from '../../lib/notify';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || '';
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || '';

// City channels (Step 6 spec). Backend canonical channel names.
const CITY_CHANNELS = [
  { id: 'All', label: 'All' },
  { id: 'CDMX', label: 'CDMX' },
  { id: 'Playa', label: 'Playa' },
  { id: 'Mérida', label: 'Mérida' },
  { id: 'Puerto Vallarta', label: 'Puerto Vallarta' },
  { id: 'SMA', label: 'SMA' },
  { id: 'Tulum', label: 'Tulum' },
];

const REACTIONS = [
  { id: 'like', icon: ThumbsUp, label: 'Like', color: '#1A5C6E' },
  { id: 'love', icon: Heart, label: 'Love', color: '#E11D48' },
  { id: 'fire', icon: Flame, label: 'Fire', color: '#E8A830' },
  { id: 'hundred', icon: Award, label: '100', color: '#2CA8A0' },
];

// iter 187.o.1 - Lazy-load the Cloudinary Upload Widget script on first use.
// The script is CDN-hosted, so we inject it once and reuse the singleton.
// Community image uploads still run through Cloudinary; the Emergent Object
// Storage migration for this surface is tracked in the backlog.
const CLOUDINARY_WIDGET_SRC = 'https://widget.cloudinary.com/v2.0/global/all.js';
let cloudinaryLoader = null;
const loadCloudinary = () => {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (window.cloudinary) return Promise.resolve(true);
  if (cloudinaryLoader) return cloudinaryLoader;
  cloudinaryLoader = new Promise((resolve) => {
    const existing = document.querySelector(`script[src="${CLOUDINARY_WIDGET_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(!!window.cloudinary));
      existing.addEventListener('error', () => resolve(false));
      return;
    }
    const script = document.createElement('script');
    script.src = CLOUDINARY_WIDGET_SRC;
    script.async = true;
    script.onload = () => resolve(!!window.cloudinary);
    script.onerror = () => {
      cloudinaryLoader = null;
      resolve(false);
    };
    document.body.appendChild(script);
  });
  return cloudinaryLoader;
};

const relativeTime = (iso) => {
  if (!iso) return '';
  const diff = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString('en-US', { timeZone: 'America/Chicago' });
};

const authHeaders = () => {
  const t = localStorage.getItem('auth_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// ============== AVATAR ==============
const Avatar = ({ user, size = 40 }) => {
  const initial = (user?.first_name || user?.name || '?')[0]?.toUpperCase() || '?';
  if (user?.profile_photo_url) {
    return (
      <img
        src={user.profile_photo_url}
        alt=""
        className="rounded-full object-cover shrink-0 ring-2 ring-white"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-[#2CA8A0] text-white font-medium flex items-center justify-center shrink-0 ring-2 ring-white"
      style={{ width: size, height: size, fontSize: size / 2.5 }}
    >
      {initial}
    </div>
  );
};

// ============== POST COMPOSER ==============
const PostComposer = ({ user, channel, onPosted, canWrite }) => {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState('');
  const [postChannel, setPostChannel] = useState(channel === 'All' ? 'CDMX' : channel);
  const [imageUrl, setImageUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (channel !== 'All') setPostChannel(channel);
  }, [channel]);

  const handleUpload = async () => {
    if (!CLOUD_NAME || !UPLOAD_PRESET) {
      notifyError('Image upload is not configured.');
      return;
    }
    const ok = await loadCloudinary();
    if (!ok) return;
    setUploading(true);
    const widget = window.cloudinary.createUploadWidget(
      {
        cloudName: CLOUD_NAME,
        uploadPreset: UPLOAD_PRESET,
        sources: ['local', 'camera'],
        multiple: false,
        maxFiles: 1,
        folder: 'mundalo/community',
      },
      (err, result) => {
        if (!err && result?.event === 'success') {
          setImageUrl(result.info.secure_url);
        }
        if (result?.event === 'close') setUploading(false);
      },
    );
    widget.open();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/community/posts`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          channel: postChannel,
          content: content.trim(),
          image_url: imageUrl,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        trackCommunityPostCreated({ board: postChannel });
        onPosted(data.post);
        setContent('');
        setImageUrl(null);
        setOpen(false);
      } else {
        const err = await res.json().catch(() => ({}));
        notifyError(err, 'Could not post.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!canWrite) {
    return (
      <div
        className="bg-white border border-[#EFE6CF] rounded-2xl p-5 mb-5"
        data-testid="composer-locked"
      >
        <div className="flex items-center gap-3">
          <Avatar user={user} size={44} />
          <div className="flex-1">
            <p className="text-[#0A1F2E] font-body">
              Posting is unlocked with Community membership.
            </p>
            <Link
              to="/portal/upgrade"
              className="inline-flex items-center gap-1 mt-1 font-mono uppercase text-[11px] tracking-wider text-[#2CA8A0] hover:text-[#1A5C6E]"
              data-testid="composer-upgrade-link"
            >
              Upgrade to join the conversation →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white border border-[#EFE6CF] rounded-2xl p-5 mb-5 transition-shadow hover:shadow-sm"
      data-testid="post-composer"
    >
      <div className="flex items-start gap-3">
        <Avatar user={user} size={44} />
        <div className="flex-1">
          {!open ? (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="w-full text-left bg-[#F9F6EF] hover:bg-[#F4ECD8] transition rounded-full px-5 py-3 text-[#0A1F2E]/60 font-body"
              data-testid="composer-trigger"
            >
              What's on your mind, {user?.first_name || 'friend'}?
            </button>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <textarea
                autoFocus
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={`Share a question, recommendation, or moment from ${postChannel}…`}
                rows={4}
                maxLength={2000}
                className="w-full px-4 py-3 bg-[#F9F6EF] border border-[#EFE6CF] rounded-xl text-[#0A1F2E] font-body resize-none focus:outline-none focus:border-[#2CA8A0]"
                data-testid="composer-input"
              />

              {imageUrl && (
                <div className="relative inline-block">
                  <img src={imageUrl} alt="" className="rounded-xl max-h-64" />
                  <button
                    type="button"
                    onClick={() => setImageUrl(null)}
                    className="absolute top-2 right-2 w-7 h-7 bg-[#0A1F2E]/70 text-white rounded-full flex items-center justify-center hover:bg-[#0A1F2E]"
                    data-testid="composer-remove-image"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                <div className="flex items-center gap-3">
                  <select
                    value={postChannel}
                    onChange={(e) => setPostChannel(e.target.value)}
                    className="text-sm font-mono uppercase tracking-wider px-3 py-1.5 bg-[#F9F6EF] border border-[#EFE6CF] rounded-full text-[#1A5C6E] cursor-pointer"
                    data-testid="composer-channel"
                  >
                    {CITY_CHANNELS.filter((c) => c.id !== 'All').map((c) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>

                  <MobilePhotoUploadButton
                    cloudName={CLOUD_NAME}
                    uploadPreset={UPLOAD_PRESET}
                    folder="mundalo/community"
                    onUploaded={(secureUrl) => setImageUrl(secureUrl)}
                    aspect={1}
                    maxKB={900}
                    title="Crop community photo"
                    className="flex items-center gap-1.5 text-sm text-[#1A5C6E] hover:text-[#2CA8A0]"
                    data-testid="composer-image-btn"
                  >
                    <ImageIcon className="w-4 h-4" />
                    Photo
                  </MobilePhotoUploadButton>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => { setOpen(false); setContent(''); setImageUrl(null); }}
                    className="px-4 py-2 text-sm text-[#0A1F2E]/60 hover:text-[#0A1F2E]"
                    data-testid="composer-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!content.trim() || submitting}
                    className="font-mono uppercase text-[11px] tracking-[0.12em] font-semibold bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] px-5 py-2.5 rounded-full transition disabled:opacity-50"
                    data-testid="composer-submit"
                  >
                    {submitting ? 'Posting…' : 'Post'}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ============== REACTION BAR ==============
const ReactionBar = ({ reactions, myReaction, onReact, locked }) => {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {REACTIONS.map(({ id, icon: Icon, label, color }) => {
        const count = reactions?.counts?.[id] ?? 0;
        const active = myReaction === id;
        return (
          <button
            key={id}
            type="button"
            disabled={locked}
            onClick={() => onReact(id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
              active
                ? 'border-transparent text-white'
                : 'bg-white border-[#EFE6CF] text-[#0A1F2E]/70 hover:border-[#2CA8A0]/50 hover:text-[#0A1F2E]'
            } ${locked ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={active ? { backgroundColor: color } : undefined}
            data-testid={`react-${id}`}
            aria-label={`React with ${label}`}
            aria-pressed={active}
          >
            <Icon className="w-3.5 h-3.5" />
            <span>{label}</span>
            {count > 0 && <span className="font-mono">{count}</span>}
          </button>
        );
      })}
    </div>
  );
};

// ============== COMMENTS ==============
const CommentSection = ({ postId, postAuthorUserId, canWrite, onCommentCountChange }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/community/posts/${postId}/comments`, {
        credentials: 'include',
        headers: authHeaders(),
      });
      if (r.ok) {
        const d = await r.json();
        setComments(d.comments || []);
      }
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSubmitting(true);

    // Optimistic
    const optimistic = {
      comment_id: `tmp_${Date.now()}`,
      content: text.trim(),
      created_at: new Date().toISOString(),
      author: { first_name: 'You' },
      author_user_id: user?.id,
      likes_count: 0,
      _optimistic: true,
    };
    setComments((prev) => [...prev, optimistic]);
    setText('');
    onCommentCountChange?.(+1);

    try {
      const r = await fetch(`${API_URL}/api/community/posts/${postId}/comments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ content: optimistic.content }),
      });
      if (r.ok) {
        const d = await r.json();
        setComments((prev) =>
          prev.map((c) => (c.comment_id === optimistic.comment_id ? d.comment : c)),
        );
      } else {
        setComments((prev) => prev.filter((c) => c.comment_id !== optimistic.comment_id));
        onCommentCountChange?.(-1);
      }
    } catch {
      setComments((prev) => prev.filter((c) => c.comment_id !== optimistic.comment_id));
      onCommentCountChange?.(-1);
    } finally {
      setSubmitting(false);
    }
  };

  // iter 177 - permission gate for the delete affordance.
  const canDeleteComment = (c) => {
    if (!user || !c?.comment_id || c._optimistic) return false;
    if (user.role === 'admin') return true;
    if (c.author_user_id && c.author_user_id === user.id) return true;
    if (postAuthorUserId && postAuthorUserId === user.id) return true;
    return false;
  };

  const handleDelete = async (comment) => {
    if (!canDeleteComment(comment)) return;
    if (deletingId) return; // block reentry
    if (!window.confirm('Delete this comment? This cannot be undone.')) return;

    setDeletingId(comment.comment_id);
    // Optimistic remove
    const previous = comments;
    setComments((prev) => prev.filter((c) => c.comment_id !== comment.comment_id));
    onCommentCountChange?.(-1);
    try {
      const r = await fetch(`${API_URL}/api/community/comments/${comment.comment_id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: authHeaders(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
    } catch (err) {
      // Rollback on failure
      setComments(previous);
      onCommentCountChange?.(+1);
      notifyError(err, 'Could not delete comment. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-[#EFE6CF]">
      {loading ? (
        <div className="space-y-3">
          {[0, 1].map((i) => (
            <div key={i} className="flex gap-2.5">
              <SkeletonAvatar size={32} />
              <Skeleton className="h-14 flex-1 rounded-xl" />
            </div>
          ))}
        </div>
      ) : comments.length === 0 ? (
        <p className="text-sm text-[#0A1F2E]/50 italic">Be the first to comment.</p>
      ) : (
        <ul className="space-y-3">
          {comments.map((c) => {
            const showDelete = canDeleteComment(c);
            return (
              <li key={c.comment_id} className="flex gap-2.5" data-testid={`comment-${c.comment_id}`}>
                <Avatar user={c.author} size={32} />
                <div className="flex-1">
                  <div className="bg-[#F9F6EF] rounded-2xl px-4 py-2.5 relative group">
                    <p className="text-sm font-medium text-[#0A1F2E]">
                      {c.author?.first_name || 'Member'}
                    </p>
                    <p className="text-sm text-[#0A1F2E]/85 mt-0.5 break-words whitespace-pre-wrap">{c.content}</p>
                    {showDelete && (
                      <button
                        type="button"
                        onClick={() => handleDelete(c)}
                        disabled={deletingId === c.comment_id}
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 disabled:opacity-100 disabled:cursor-wait text-[#0A1F2E]/45 hover:text-[#B94A3B] transition"
                        data-testid={`comment-delete-${c.comment_id}`}
                        aria-label="Delete comment"
                        title="Delete comment"
                      >
                        {deletingId === c.comment_id
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <Trash2 className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                  <p className="text-[11px] text-[#0A1F2E]/45 mt-1 ml-2">
                    {relativeTime(c.created_at)}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {canWrite && (
        <form onSubmit={submit} className="flex items-start gap-2 mt-4">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write a comment…"
            rows={1}
            onInput={(e) => {
              e.target.style.height = 'auto';
              e.target.style.height = `${Math.min(e.target.scrollHeight, 240)}px`;
            }}
            onKeyDown={(e) => {
              // Submit on Enter, newline on Shift+Enter (native textarea behavior).
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit(e);
              }
            }}
            className="flex-1 bg-[#F9F6EF] border border-[#EFE6CF] rounded-2xl px-4 py-2 text-sm text-[#0A1F2E] focus:outline-none focus:border-[#2CA8A0] resize-none leading-relaxed"
            data-testid={`comment-input-${postId}`}
          />
          <button
            type="submit"
            disabled={!text.trim() || submitting}
            className="w-9 h-9 bg-[#2CA8A0] hover:brightness-95 disabled:opacity-40 text-white rounded-full flex items-center justify-center transition shrink-0"
            data-testid={`comment-submit-${postId}`}
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </form>
      )}
    </div>
  );
};

// ============== POST CARD ==============
const PostCard = ({ post: initial, canWrite }) => {
  const [post, setPost] = useState(initial);
  const [openComments, setOpenComments] = useState(false);

  useEffect(() => setPost(initial), [initial]);

  const handleReact = async (type) => {
    if (!canWrite) return;
    // Optimistic update
    const prev = post;
    const cur = post.reactions || { counts: {}, my_reaction: null, total: 0 };
    const my = cur.my_reaction;
    const counts = { ...cur.counts };
    if (my === type) {
      counts[type] = Math.max(0, (counts[type] || 0) - 1);
    } else {
      if (my) counts[my] = Math.max(0, (counts[my] || 0) - 1);
      counts[type] = (counts[type] || 0) + 1;
    }
    const newMy = my === type ? null : type;
    const total = Object.values(counts).reduce((s, n) => s + n, 0);
    setPost({ ...post, reactions: { counts, my_reaction: newMy, total } });

    try {
      const r = await fetch(`${API_URL}/api/community/posts/${post.post_id}/react`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ type }),
      });
      if (r.ok) {
        const d = await r.json();
        setPost((p) => ({ ...p, reactions: d.reactions }));
      } else {
        setPost(prev);
      }
    } catch {
      setPost(prev);
    }
  };

  return (
    <article
      className="bg-white border border-[#EFE6CF] rounded-2xl p-5 transition-shadow hover:shadow-sm"
      data-testid={`post-${post.post_id}`}
    >
      {/* Header */}
      <header className="flex items-start gap-3 mb-3">
        <Avatar user={post.author} size={44} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-[#0A1F2E]">
              {post.author?.first_name || 'Member'}
              {post.author?.last_name_initial ? ` ${post.author.last_name_initial}.` : ''}
            </span>
            {post.author?.tier_badge && (
              <span
                className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#2CA8A0]/10 text-[#1A5C6E]"
                data-testid="post-tier-badge"
              >
                {post.author.tier_badge}
              </span>
            )}
          </div>
          <p className="text-xs text-[#0A1F2E]/55 mt-0.5">
            {post.author?.city ? `${post.author.city} · ` : ''}
            {relativeTime(post.created_at)} · <span className="font-mono uppercase tracking-wider text-[#2CA8A0]">{post.channel}</span>
          </p>
        </div>
      </header>

      {/* Body */}
      <p className="text-[#0A1F2E]/90 font-body leading-relaxed whitespace-pre-wrap break-words">
        {post.content}
      </p>

      {post.image_url && (
        <img
          src={post.image_url}
          alt=""
          className="mt-3 rounded-xl w-full max-h-[480px] object-cover"
          loading="lazy"
        />
      )}

      {/* Action row */}
      <div className="flex items-center justify-between mt-4">
        <ReactionBar
          reactions={post.reactions}
          myReaction={post.reactions?.my_reaction}
          onReact={handleReact}
          locked={!canWrite}
        />
        <button
          type="button"
          onClick={() => setOpenComments((v) => !v)}
          className="flex items-center gap-1.5 text-xs text-[#0A1F2E]/70 hover:text-[#2CA8A0] transition"
          data-testid={`toggle-comments-${post.post_id}`}
        >
          <MessageCircle className="w-4 h-4" />
          {post.comment_count > 0 ? `${post.comment_count}` : 'Comment'}
        </button>
      </div>

      {openComments && (
        <CommentSection
          postId={post.post_id}
          postAuthorUserId={post.author_user_id}
          canWrite={canWrite}
          onCommentCountChange={(d) =>
            setPost((p) => ({ ...p, comment_count: Math.max(0, (p.comment_count || 0) + d) }))
          }
        />
      )}
    </article>
  );
};

// ============== SIDEBARS ==============
const TrendingSidebar = () => {
  const [trending, setTrending] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/community/trending`, {
      credentials: 'include',
      headers: authHeaders(),
    })
      .then((r) => (r.ok ? r.json() : { trending: [] }))
      .then((d) => setTrending(d.trending || []))
      .catch(() => setTrending([]));
  }, []);

  return (
    <aside
      className="bg-white border border-[#EFE6CF] rounded-2xl p-5"
      data-testid="trending-sidebar"
    >
      <h3 className="flex items-center gap-2 font-mono uppercase text-[11px] tracking-[0.15em] text-[#1A5C6E] mb-4">
        <TrendingUp className="w-4 h-4" />
        Trending this week
      </h3>
      {trending === null ? (
        <div className="space-y-2.5">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full rounded-xl" />)}
        </div>
      ) : trending.length === 0 ? (
        <p className="text-sm text-[#0A1F2E]/55 italic">
          No trending channels yet. Be the first to start a conversation.
        </p>
      ) : (
        <ul className="space-y-2.5">
          {trending.map((t) => (
            <li
              key={t.channel}
              className="flex items-center justify-between p-2.5 rounded-xl hover:bg-[#F9F6EF] transition"
            >
              <div>
                <p className="font-medium text-[#0A1F2E]">{t.channel}</p>
                <p className="text-[11px] text-[#0A1F2E]/55">
                  {t.post_count} posts · {t.comment_count} comments
                </p>
              </div>
              <span className="text-[#E8A830] font-mono text-sm">{t.score}</span>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
};

const SuggestedMembersSidebar = () => {
  const [members, setMembers] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/community/suggested-members`, {
      credentials: 'include',
      headers: authHeaders(),
    })
      .then((r) => (r.ok ? r.json() : { suggested: [] }))
      .then((d) => setMembers(d.suggested || []))
      .catch(() => setMembers([]));
  }, []);

  return (
    <aside
      className="bg-white border border-[#EFE6CF] rounded-2xl p-5"
      data-testid="suggested-members-sidebar"
    >
      <h3 className="flex items-center gap-2 font-mono uppercase text-[11px] tracking-[0.15em] text-[#1A5C6E] mb-4">
        <Users className="w-4 h-4" />
        Members you might know
      </h3>
      {members === null ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <SkeletonAvatar size={40} />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-2.5 w-16" />
              </div>
            </div>
          ))}
        </div>
      ) : members.length === 0 ? (
        <p className="text-sm text-[#0A1F2E]/55 italic">
          New members will appear here as the community grows.
        </p>
      ) : (
        <ul className="space-y-3">
          {members.map((m) => (
            <li key={m.user_id}>
              <Link
                to={`/portal/members/${m.user_id}`}
                className="flex items-center gap-3 p-2 -mx-2 rounded-xl hover:bg-[#F9F6EF] transition"
                data-testid={`suggested-member-${m.user_id}`}
              >
                <Avatar user={m} size={40} />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-[#0A1F2E] truncate">
                    {m.first_name}{m.last_name_initial ? ` ${m.last_name_initial}.` : ''}
                  </p>
                  <p className="text-[11px] text-[#0A1F2E]/55 truncate">
                    {m.tier_badge && <span className="text-[#1A5C6E]">{m.tier_badge}</span>}
                    {m.destination_city && (
                      <>
                        {m.tier_badge ? ' · ' : ''}
                        {m.destination_city}
                      </>
                    )}
                  </p>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
};

// ============== CITY CHANNEL TABS ==============
const ChannelTabs = ({ active, onChange }) => (
  <div
    className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 mb-5 no-scrollbar"
    data-testid="city-channel-tabs"
  >
    {CITY_CHANNELS.map((c) => {
      const isActive = active === c.id;
      return (
        <button
          key={c.id}
          type="button"
          onClick={() => onChange(c.id)}
          className={`px-4 py-2 rounded-full whitespace-nowrap transition-all font-mono uppercase text-[11px] tracking-[0.12em] ${
            isActive
              ? 'bg-[#0A1F2E] text-[#F9F6EF]'
              : 'bg-white text-[#0A1F2E]/65 hover:text-[#0A1F2E] border border-[#EFE6CF]'
          }`}
          data-testid={`city-tab-${c.id}`}
        >
          {c.label}
        </button>
      );
    })}
  </div>
);

// ============== MAIN ==============
const PortalCommunity = () => {
  const { user } = useAuth();
  const [channel, setChannel] = useState('All');
  const [posts, setPosts] = useState(null);
  const [canWrite, setCanWrite] = useState(false);
  const [errMsg, setErrMsg] = useState(null);
  const lastChannelRef = useRef(channel);

  const load = useCallback(async () => {
    setPosts(null);
    setErrMsg(null);
    try {
      const url = `${API_URL}/api/community/posts?channel=${encodeURIComponent(channel)}`;
      const r = await fetch(url, { credentials: 'include', headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        setPosts(d.posts || []);
        setCanWrite(!!d.can_write);
      } else if (r.status === 403) {
        const err = await r.json().catch(() => ({}));
        setErrMsg(err.detail?.message || 'Upgrade to access the Mundalo Community.');
        setPosts([]);
      } else {
        setPosts([]);
      }
    } catch {
      setPosts([]);
    }
  }, [channel]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePosted = useCallback((newPost) => {
    setPosts((prev) => [newPost, ...(prev || [])]);
  }, []);

  // Smooth fade transition between channels
  useEffect(() => {
    lastChannelRef.current = channel;
  }, [channel]);

  const skeletonList = useMemo(
    () => [0, 1, 2].map((i) => <SkeletonPost key={i} />),
    [],
  );

  return (
    <PortalLayout activeTab="communityBoard">
      <SEO
        title="Community"
        description="Connect with Mundalo members across CDMX, Playa, Mérida, Puerto Vallarta, San Miguel, and Tulum."
        path="/portal/community"
        noindex
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
        <GuidedUpsell variant="card" storageKey="community" />

        {/* Page header */}
        <div className="mb-6">
          <h1
            className="font-display italic text-4xl md:text-5xl text-[#0A1F2E] leading-tight"
            data-testid="community-heading"
          >
            Community
          </h1>
          <p className="text-[#0A1F2E]/65 mt-1 font-body">
            Real conversations from people building a life across borders.
          </p>
        </div>

        {/* City channel tabs */}
        <ChannelTabs active={channel} onChange={setChannel} />

        {/* Optional 403 strip */}
        {errMsg && (
          <div
            className="bg-[#F4ECD8] border border-[#E8A830]/40 rounded-2xl p-4 mb-5 flex items-center gap-3"
            data-testid="community-locked"
          >
            <Sparkles className="w-5 h-5 text-[#E8A830] shrink-0" />
            <p className="text-sm text-[#0A1F2E]">{errMsg}</p>
            <Link
              to="/portal/upgrade"
              className="ml-auto font-mono uppercase text-[11px] tracking-[0.12em] font-semibold bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] px-4 py-2 rounded-full transition"
              data-testid="community-upgrade-cta"
            >
              Upgrade
            </Link>
          </div>
        )}

        {/* 3-column layout: feed (8) + sidebars (4) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* FEED */}
          <div className="lg:col-span-8 space-y-5">
            <PostComposer
              user={user}
              channel={channel}
              onPosted={handlePosted}
              canWrite={canWrite}
            />

            <div
              key={channel}
              className="space-y-4 transition-opacity duration-300"
              style={{ animation: 'fadeIn 0.3s ease-out' }}
            >
              {posts === null ? (
                skeletonList
              ) : posts.length === 0 && !errMsg ? (
                <div
                  className="bg-white border border-dashed border-[#EFE6CF] rounded-2xl p-10 text-center"
                  data-testid="community-empty"
                >
                  <p className="font-display italic text-2xl text-[#0A1F2E] mb-2">
                    No posts in {channel === 'All' ? 'the feed' : channel} yet.
                  </p>
                  <p className="text-[#0A1F2E]/65">
                    {canWrite
                      ? 'Be the first to share something.'
                      : 'Check back soon as more members join.'}
                  </p>
                </div>
              ) : (
                posts.map((p) => (
                  <PostCard key={p.post_id} post={p} canWrite={canWrite} />
                ))
              )}
            </div>
          </div>

          {/* SIDEBARS */}
          <aside className="lg:col-span-4 space-y-5">
            <TrendingSidebar />
            <SuggestedMembersSidebar />
          </aside>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { scrollbar-width: none; }
      `}</style>
    </PortalLayout>
  );
};

export default PortalCommunity;
